import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import MessageLoading from './message-loading'
import { Button, type ButtonProps } from '../ui/button'

// ChatBubble
const chatBubbleVariant = cva('flex gap-2 max-w-[80%] items-end relative group', {
  variants: {
    variant: {
      received: 'self-start',
      sent: 'self-end flex-row-reverse',
    },
    layout: {
      default: '',
      ai: 'max-w-full w-full items-center',
    },
  },
  defaultVariants: {
    variant: 'received',
    layout: 'default',
  },
})

type ChatBubbleVariantProps = VariantProps<typeof chatBubbleVariant>

type ChatBubbleProps = React.ComponentProps<'div'> & ChatBubbleVariantProps

const ChatBubble = ({ className, variant, layout, children, ...props }: ChatBubbleProps) => (
  <div
    className={cn(chatBubbleVariant({ variant, layout, className }), 'relative group')}
    {...props}
  >
    {/* Composite children (avatar, message) inherit the bubble's variant and layout. */}
    {React.Children.map(children, (child) =>
      React.isValidElement<ChatBubbleVariantProps>(child) && typeof child.type !== 'string'
        ? React.cloneElement(child, {
            variant,
            layout,
          })
        : child
    )}
  </div>
)
ChatBubble.displayName = 'ChatBubble'

type ChatBubbleAvatarProps = {
  src: string
  fallback: React.ReactNode
  className?: string
}

const ChatBubbleAvatar = ({ src, fallback, className }: ChatBubbleAvatarProps) => (
  <Avatar className={className}>
    <AvatarImage src={src} alt="Avatar" />
    <AvatarFallback>{fallback}</AvatarFallback>
  </Avatar>
)

// ChatBubbleMessage
const chatBubbleMessageVariants = cva('p-4', {
  variants: {
    variant: {
      received: 'bg-secondary text-secondary-foreground rounded-r-lg rounded-tl-lg',
      sent: 'bg-action text-action-foreground rounded-l-lg rounded-tr-lg',
    },
    layout: {
      default: '',
      ai: 'border-t w-full rounded-none bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'received',
    layout: 'default',
  },
})

type ChatBubbleMessageProps = React.ComponentProps<'div'> &
  VariantProps<typeof chatBubbleMessageVariants> & {
    isLoading?: boolean
  }

const ChatBubbleMessage = ({
  className,
  variant,
  layout,
  isLoading = false,
  children,
  ...props
}: ChatBubbleMessageProps) => (
  <div
    className={cn(
      chatBubbleMessageVariants({ variant, layout, className }),
      'break-words max-w-full whitespace-pre-wrap'
    )}
    {...props}
  >
    {isLoading ? (
      <div className="flex items-center space-x-2">
        <MessageLoading />
      </div>
    ) : (
      children
    )}
  </div>
)
ChatBubbleMessage.displayName = 'ChatBubbleMessage'

type ChatBubbleTimestampProps = React.ComponentProps<'div'> & {
  timestamp: React.ReactNode
}

const ChatBubbleTimestamp = ({ timestamp, className, ...props }: ChatBubbleTimestampProps) => (
  <div className={cn('text-xs mt-2 text-right', className)} {...props}>
    {timestamp}
  </div>
)

type ChatBubbleActionProps = ButtonProps & {
  icon: React.ReactNode
}

const ChatBubbleAction = ({
  icon,
  onClick,
  className,
  variant = 'ghost',
  size = 'icon',
  ...props
}: ChatBubbleActionProps) => (
  <Button variant={variant} size={size} className={className} onClick={onClick} {...props}>
    {icon}
  </Button>
)

type ChatBubbleActionWrapperProps = React.ComponentProps<'div'> & {
  variant?: 'sent' | 'received'
}

const ChatBubbleActionWrapper = ({
  variant,
  className,
  children,
  ...props
}: ChatBubbleActionWrapperProps) => (
  <div
    className={cn(
      'absolute top-1/2 -translate-y-1/2 flex opacity-0 group-hover:opacity-100 transition-opacity duration-200',
      variant === 'sent'
        ? '-left-1 -translate-x-full flex-row-reverse'
        : '-right-1 translate-x-full',
      className
    )}
    {...props}
  >
    {children}
  </div>
)
ChatBubbleActionWrapper.displayName = 'ChatBubbleActionWrapper'

export {
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleMessage,
  ChatBubbleTimestamp,
  chatBubbleVariant,
  chatBubbleMessageVariants,
  ChatBubbleAction,
  ChatBubbleActionWrapper,
}
